import { useState } from 'react';
import { ChevronRight, Clock, Truck, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import DateSelector from './DateSelector';
import DriverExpressDetail from './DriverExpressDetail';
import DriverExpressMap from './DriverExpressMap';

const STATUS_CONFIG = {
  pending:    { label: 'Pendiente',  color: 'bg-yellow-100 text-yellow-800', Icon: Clock,         order: 0 },
  encamino:   { label: 'En camino',  color: 'bg-blue-100 text-blue-800',     Icon: Truck,         order: 1 },
  entregado:  { label: 'Entregado',  color: 'bg-green-100 text-green-800',   Icon: CheckCircle,   order: 2 },
  rechazado:  { label: 'Rechazado',  color: 'bg-red-100 text-red-800',       Icon: AlertTriangle, order: 3 },
  cancelado:  { label: 'Cancelado',  color: 'bg-gray-100 text-gray-500',     Icon: Clock,         order: 4 },
  postergado: { label: 'Postergado', color: 'bg-purple-100 text-purple-800', Icon: Clock,         order: 5 },
};

// Group deliveries by status, active ones first
function groupByStatus(deliveries) {
  const groups = {};
  const activeStatuses = ['pending', 'encamino'];
  const completedStatuses = ['entregado', 'rechazado', 'cancelado', 'postergado'];
  const order = [...activeStatuses, ...completedStatuses];

  deliveries.forEach(d => {
    if (!groups[d.status]) groups[d.status] = [];
    groups[d.status].push(d);
  });

  return order
    .filter(s => groups[s] && groups[s].length > 0)
    .map(s => ({ status: s, items: groups[s] }));
}

function DeliveryCard({ delivery, onOpenDetail, onOpenMap }) {
  const cfg = STATUS_CONFIG[delivery.status] || STATUS_CONFIG.pending;
  const StatusIcon = cfg.Icon;

  return (
    <button
      onClick={() => onOpenDetail(delivery)}
      className="w-full bg-white rounded-xl border border-gray-200 p-4 text-left active:bg-gray-50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Status + id */}
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
              <StatusIcon size={10} />
              {cfg.label}
            </span>
            <span className="text-xs text-gray-400 font-mono">#{delivery.id}</span>
          </div>

          {/* Recipient */}
          <div className="text-sm font-semibold text-gray-800">{delivery.recipient_name}</div>

          {/* Addresses */}
          <div className="text-xs text-gray-500 truncate">{delivery.pickup_address}</div>
          <div className="text-xs text-gray-400 truncate">→ {delivery.dropoff_address}</div>

          {/* Meta row */}
          <div className="flex items-center gap-3 text-xs text-gray-400 pt-0.5">
            <span>{delivery.distance_km} km</span>
            <span>·</span>
            <span className="font-semibold text-brand-green">
              S/ {Number(delivery.driver_earnings).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <ChevronRight size={16} className="text-gray-300" />
          {/* Map shortcut for active deliveries */}
          {(delivery.status === 'pending' || delivery.status === 'encamino') && (
            <button
              onClick={e => { e.stopPropagation(); onOpenMap(delivery); }}
              className="text-xs text-brand-primary font-medium bg-brand-primary/10 px-2 py-1 rounded-lg active:scale-95 transition-transform"
            >
              Mapa
            </button>
          )}
        </div>
      </div>
    </button>
  );
}

export default function DriverExpressList({
  user_id,
  deliveries,
  loading,
  selectedDate,
  onDateChange,
  updateDelivery,
  onRefresh,
}) {
  const [showCalendar, setShowCalendar] = useState(false);
  const [detailDelivery, setDetailDelivery] = useState(null);
  const [mapDelivery, setMapDelivery]       = useState(null);

  const grouped = groupByStatus(deliveries);

  return (
    <div className="p-4 space-y-4">

      {/* Date selector + refresh */}
      <div className="flex gap-2 items-start">
        <div className="flex-1">
          <DateSelector
            selectedDate={selectedDate}
            onDateChange={(d) => { setShowCalendar(false); onDateChange(d); }}
            showCalendar={showCalendar}
            onToggleCalendar={() => setShowCalendar(v => !v)}
          />
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="mt-1 p-2.5 bg-white border border-gray-200 rounded-xl text-gray-500 active:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Summary */}
      {!loading && (
        <div className="flex gap-3 text-xs text-gray-500 px-1">
          <span>{deliveries.length} entrega{deliveries.length !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span className="text-brand-green font-semibold">
            S/ {deliveries
              .filter(d => d.status === 'entregado')
              .reduce((s, d) => s + Number(d.driver_earnings), 0)
              .toFixed(2)} ganados
          </span>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl h-28 animate-pulse border border-gray-100" />
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center">
          <div className="text-gray-300 text-4xl mb-3">📭</div>
          <div className="text-gray-500 font-medium">Sin entregas para esta fecha</div>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(({ status, items }) => {
            const cfg = STATUS_CONFIG[status];
            return (
              <div key={status} className="space-y-2">
                <div className={`flex items-center gap-2 px-1`}>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>
                    {cfg.label}
                  </span>
                  <span className="text-xs text-gray-400">{items.length}</span>
                </div>
                {items.map(d => (
                  <DeliveryCard
                    key={d.id}
                    delivery={d}
                    onOpenDetail={setDetailDelivery}
                    onOpenMap={setMapDelivery}
                  />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {detailDelivery && (
        <DriverExpressDetail
          delivery={detailDelivery}
          user_id={user_id}
          onClose={() => setDetailDelivery(null)}
          onUpdate={(updated) => {
            updateDelivery(updated);
            setDetailDelivery(prev => ({ ...prev, ...updated }));
          }}
          onOpenMap={() => {
            setMapDelivery(detailDelivery);
            setDetailDelivery(null);
          }}
        />
      )}

      {/* Map modal */}
      {mapDelivery && (
        <DriverExpressMap
          delivery={mapDelivery}
          user_id={user_id}
          onClose={() => setMapDelivery(null)}
        />
      )}
    </div>
  );
}